import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlobalContext } from '@/app/providers/globalState';
import { Button, TruncatedText, SparkIcon, SparkModalPortal, Modal, sparkToast, Checkbox } from '@spark';
const SpinnerRing = ({ size = 20, strokeWidth, trackColor = '#E5E7EB', arcColor = '#3B82F6', arcFraction = 0.3, className, style }) => {
    const stroke = strokeWidth ?? Math.max(2, Math.round(size / 10));
    const center = size / 2;
    const radius = center - stroke;
    const circumference = 2 * Math.PI * radius;
    const arc = circumference * arcFraction;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className={`spinner-ring${className ? ` ${className}` : ''}`} style={style}>
            <circle cx={center} cy={center} r={radius} fill="none" stroke={trackColor} strokeWidth={stroke} />
            <circle cx={center} cy={center} r={radius} fill="none" stroke={arcColor} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${arc} ${circumference - arc}`} transform={`rotate(-90 ${center} ${center})`} />
        </svg>
    );
};
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import { buildLabelGenFailureDetailLines } from '@/features/datasets/utils/labelGenerationWarning';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { displayAiGeneratedDatasetName, isAiGeneratedDatasetName } from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import { displayPublicDatasetName, isPublicDatasetName } from '@/features/datasets/utils/publicDatasetNames';
import {
    deriveDownloadSteps,
    isProgressFileActive,
    isProgressFileComplete,
    isProgressFileWarning,
} from '@/shared/utils/downloadProgressUtils';
import {
    deriveDatasetStepStates,
    generationStepLabel,
    isActiveGenerationCard,
    LABEL_GEN_WAIT_FOR_MODEL_CONFIG,
    TRAJECTORY_GEN_WAIT_FOR_MODEL_CONFIG,
} from '@/features/datasets/utils/datasetProgressSteps';
import {
    buildTrajectorySkipDetailLines,
    shouldOfferKeepGeneratedRecords,
} from '@/features/datasets/utils/generationProgressStatus';
import {
    deriveRetrievalAgentSteps,
    queuedWaitingSubtitle,
    shouldShowRetrievalUploadGroup,
} from '@/features/datasets/utils/retrievalAgentProgress';

/** Human-readable size string for a byte count, used to surface artifact size in the UI. */
function formatBytes(bytes) {
    if (bytes === null || bytes === undefined || Number.isNaN(bytes) || bytes <= 0) {
        return '';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIdx = 0;
    while (value >= 1024 && unitIdx < units.length - 1) {
        value /= 1024;
        unitIdx += 1;
    }
    const digits = value >= 10 || unitIdx === 0 ? 0 : 1;
    return `${value.toFixed(digits)} ${units[unitIdx]}`;
}

const COLORS = {
    headerText: '#111827',
    controlIcon: '#1E293B',
    activeText: '#111827',
    mutedText: '#6B7280',
    pendingText: '#9CA3AF',
    ringMuted: '#D1D5DB',
    ringTrack: '#E5E7EB',
    primaryBlue: '#3B82F6',
    successGreen: '#4CAF50',
    error: '#DC2626',
    warning: '#D97706',
    cardBg: '#FFFFFF',
};

const CARD_WIDTH = 324;
const TOP_NAV_OFFSET = 72;
const VIEWPORT_MARGIN = 24;

// The bar must stay visible above the task / model-details drawers (zIndex
// 2000) and the download / configure-deployment modal chain (zIndex 2100),
// so progress feedback shows immediately after the user confirms a download.
const BAR_Z_INDEX = 2200;
// Tooltips / popconfirms spawned from inside the bar render in their own
// portal, so they need to sit above the bar itself.
const BAR_OVERLAY_Z_INDEX = BAR_Z_INDEX + 100;

/** User-visible dataset title in the bar (hide per-subtask ``__t_<id>`` clone suffixes). */
function progressBarDisplayName(name) {
    if (typeof name !== 'string' || !name) return '';
    const userFacing = getUserFacingDatasetName(name) || name;
    if (isAiGeneratedDatasetName(userFacing)) {
        return displayAiGeneratedDatasetName(userFacing);
    }
    if (isPublicDatasetName(userFacing)) {
        return displayPublicDatasetName(userFacing);
    }
    return userFacing;
}

function IconPendingRing() {
    return (
        <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden>
            <circle cx="10" cy="10" r="8.5" fill="none" stroke={COLORS.ringMuted} strokeWidth="1.5" />
        </svg>
    );
}

function IconSpinner() {
    return <SpinnerRing size={20} trackColor={COLORS.ringTrack} arcColor={COLORS.primaryBlue} />;
}

function IconSuccessCheck() {
    return (
        <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden style={{ display: 'block' }}>
            <circle cx="10" cy="10" r="8.5" fill="none" stroke={COLORS.successGreen} strokeWidth="1.5" />
            <path
                d="M6 10 L9 13 L14 7"
                fill="none"
                stroke={COLORS.successGreen}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconErrorMark() {
    return (
        <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden style={{ display: 'block' }}>
            <circle cx="10" cy="10" r="8.5" fill="none" stroke={COLORS.error} strokeWidth="1.5" />
            <path d="M7 7 L13 13 M13 7 L7 13" stroke={COLORS.error} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

/**
 * @param {'pending' | 'active' | 'complete' | 'error'} state
 * @param {React.ReactNode} rightSlot
 * @param {string} label
 */
function StepRow({ state, label, rightSlot, subtitle }) {
    const icon =
        state === 'complete' ? (
            <IconSuccessCheck />
        ) : state === 'active' ? (
            <IconSpinner />
        ) : state === 'error' ? (
            <IconErrorMark />
        ) : state === 'warning' ? (
            <span style={{ color: COLORS.warning, display: 'inline-flex' }}>
                <SparkIcon slug="circle-alert-triangle" size="small" />
            </span>
        ) : (
            <IconPendingRing />
        );

    const labelColor =
        state === 'pending' ? COLORS.pendingText : state === 'complete' ? COLORS.mutedText : COLORS.activeText;
    const labelWeight = state === 'active' ? 600 : 500;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 36,
            }}
        >
            <span style={{ flexShrink: 0, width: 20, display: 'flex', justifyContent: 'center' }}>{icon}</span>
            <span
                style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 14,
                    lineHeight: '20px',
                    color: labelColor,
                    fontWeight: labelWeight,
                }}
            >
                {label}
                {subtitle ? (
                    <span
                        style={{
                            display: 'block',
                            marginTop: 2,
                            fontSize: 12,
                            lineHeight: '16px',
                            fontWeight: 400,
                            color: COLORS.mutedText,
                        }}
                    >
                        {subtitle}
                    </span>
                ) : null}
            </span>
            <span
                style={{
                    flexShrink: 0,
                    fontSize: 14,
                    lineHeight: '20px',
                    color:
                        state === 'pending'
                            ? COLORS.pendingText
                            : state === 'active'
                                ? COLORS.activeText
                                : state === 'error'
                                    ? COLORS.error
                                    : state === 'warning'
                                        ? COLORS.warning
                                        : COLORS.mutedText,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {rightSlot}
            </span>
        </div>
    );
}

function stepPercentRight(state, percent, pctActiveStyle) {
    if (state === 'pending') return '0%';
    if (state === 'active') {
        return <span style={pctActiveStyle}>{`${percent}%`}</span>;
    }
    if (state === 'complete') return '100%';
    return <span style={{ color: COLORS.error }}>!</span>;
}

function deriveDatasetSteps(file) {
    const {
        understandState,
        understandPctDisplay,
        uploadState,
        processingState,
        uploadPctDisplay,
        processingPctDisplay,
        labelGenState,
        labelGenPct,
        waitingCopy,
        generationSubtitle,
    } = deriveDatasetStepStates(file);

    const pctActiveStyle = { fontWeight: 600, fontVariantNumeric: 'tabular-nums' };

    const understandRight = understandState
        ? stepPercentRight(understandState, understandPctDisplay, pctActiveStyle)
        : null;

    const uploadRight =
        uploadState === 'pending' ? (
            '0%'
        ) : uploadState === 'active' ? (
            <span style={pctActiveStyle}>{`${uploadPctDisplay}%`}</span>
        ) : uploadState === 'complete' ? (
            '100%'
        ) : (
            <span style={{ color: COLORS.error }}>!</span>
        );

    const processingRight =
        processingState === 'pending' ? (
            '0%'
        ) : processingState === 'active' ? (
            <span style={pctActiveStyle}>{`${processingPctDisplay}%`}</span>
        ) : processingState === 'complete' ? (
            '100%'
        ) : (
            <span style={{ color: COLORS.error }}>!</span>
        );

    const labelGenRight = labelGenState === 'active'
        ? <span style={{ ...pctActiveStyle, color: COLORS.activeText }}>
            {labelGenPct > 0 ? `${Math.round(labelGenPct)}%` : 'In progress'}
        </span>
        : labelGenState === 'complete' ? '100%'
            : labelGenState === 'pending' ? '0%'
            : labelGenState === 'waiting' ? null
            : labelGenState === 'warning' ? (
                <span style={{ color: COLORS.warning, display: 'inline-flex' }}>
                    <SparkIcon slug="circle-alert-triangle" size="small" />
                </span>
            )
                : labelGenState === 'error' ? <span style={{ color: COLORS.error }}>!</span>
                    : null;

    return {
        understandState,
        understandRight,
        uploadState,
        processingState,
        uploadRight,
        processingRight,
        labelGenState,
        labelGenRight,
        waitingCopy,
        generationSubtitle,
    };
}

function DatasetUploadStepRows({ file, isGeneration }) {
    const {
        understandState,
        understandRight,
        uploadState,
        processingState,
        uploadRight,
        processingRight,
        labelGenState,
        labelGenRight,
        waitingCopy,
        generationSubtitle,
    } = deriveDatasetSteps(file);
    const isQueuedGeneration = Boolean(
        isGeneration && file.isQueued && !file.isError && !file.isCompleted
    );
    // Agentic TRAJECTORY run on a standalone Generate-with-AI card: the
    // "generating" step fills trajectories into existing rows.
    const isTrajectoryGeneration = Boolean(file.isTrajectoryGeneration);
    const generatingLabel = isTrajectoryGeneration ? 'Generating trajectories' : 'Generating examples';
    if (isQueuedGeneration) {
        return (
            <>
                <StepRow
                    state="active"
                    label="Queued"
                    subtitle={queuedWaitingSubtitle(file.jobsAhead)}
                    rightSlot={null}
                />
                <StepRow state="pending" label={generatingLabel} rightSlot="0%" />
                <StepRow state="pending" label="Checking labels" rightSlot="0%" />
            </>
        );
    }
    return (
        <>
            {understandState ? (
                <StepRow
                    state={understandState}
                    label="Understanding the task"
                    rightSlot={understandRight}
                />
            ) : null}
            <StepRow
                state={uploadState}
                label={isGeneration ? generatingLabel : 'Uploading dataset'}
                subtitle={isGeneration ? generationSubtitle : undefined}
                rightSlot={uploadRight}
            />
            <StepRow
                state={processingState}
                label={isGeneration ? 'Checking labels' : 'Processing dataset'}
                rightSlot={processingRight}
            />
            {labelGenState === 'waiting' ? (
                <LabelGenerationWaitingRow
                    title={generationStepLabel(file)}
                    copy={
                        waitingCopy ||
                        (isTrajectoryGeneration
                            ? TRAJECTORY_GEN_WAIT_FOR_MODEL_CONFIG
                            : LABEL_GEN_WAIT_FOR_MODEL_CONFIG)
                    }
                />
            ) : labelGenState ? (
                <StepRow
                    state={labelGenState}
                    label={generationStepLabel(file)}
                    subtitle={isGeneration ? undefined : generationSubtitle}
                    rightSlot={labelGenRight}
                />
            ) : null}
        </>
    );
}

/** Deferred wizard AI labeling / trajectory generation: explain why it has not started yet. */
function LabelGenerationWaitingRow({ copy, title = 'Generating labels' }) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                minHeight: 36,
                paddingTop: 2,
            }}
        >
            <span
                style={{
                    flexShrink: 0,
                    width: 20,
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 2,
                    color: COLORS.primaryBlue,
                }}
            >
                <SparkIcon slug="circle-info" size="small" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        color: COLORS.activeText,
                        fontWeight: 500,
                    }}
                >
                    {title}
                </div>
                <div
                    style={{
                        marginTop: 2,
                        fontSize: 12,
                        lineHeight: '16px',
                        color: COLORS.mutedText,
                    }}
                >
                    {copy}
                </div>
            </div>
        </div>
    );
}

function DatasetProgressCard({ file, onRemove, onCancelUpload, onCancelGeneration, showDatasetSubtitle, isLast }) {
    const { progressKey, datasetName, isError, message, details, isDownload, isGeneration, isRetrievalAgent, isWarning, warningMessage } = file;
    const { triggerModelDownloadSave, allUsecases } = useContext(GlobalContext) || {};
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const isActiveUpload =
        !file.isError && !file.isCompleted && file.progress < 100 && !file.isDownload && !file.isGeneration && !file.isRetrievalAgent;
    const isActiveGeneration = isActiveGenerationCard(file);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [warningDetailsOpen, setWarningDetailsOpen] = useState(false);
    const [saveStarting, setSaveStarting] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [keepGeneratedRecords, setKeepGeneratedRecords] = useState(true);
    const showKeepRecordsCheckbox = shouldOfferKeepGeneratedRecords(file);

    // Partial label-generation warning: the failed rows were reverted to the
    // "Unlabeled" bucket, so link straight to the Data Editor pre-filtered on
    // it (?labelFilter=unlabeled). Inside the creation wizard (?step=...) that
    // means switching to the data-editor step; anywhere else, navigate to the
    // task page's Data Editor drawer.
    const failureDetailLines = buildLabelGenFailureDetailLines({
        failureBreakdown: file.failureBreakdown,
        failureDetails: file.failureDetails,
    });
    // Completed agentic run that skipped rows: `summary` is the warning body
    // and `skipped_reasons` (reason -> count) feeds the details expander.
    const skipDetailLines = buildTrajectorySkipDetailLines(file.skippedReasons);
    const warningDetailLines = failureDetailLines.length > 0 && !file.warningTitle
        ? failureDetailLines
        : skipDetailLines;
    const reviewUnlabeledTab = file.isBenchmark ? 'benchmark' : 'train-set';
    const isInCreationWizard = searchParams.has('step');
    const reviewUnlabeledUsecase = (allUsecases || []).find(
        (usecase) => usecase._id === file.usecaseId
    );
    const canReviewUnlabeled = Boolean(
        file.usecaseId && (isInCreationWizard || reviewUnlabeledUsecase)
    );
    const handleReviewUnlabeled = () => {
        if (isInCreationWizard) {
            const next = new URLSearchParams(searchParams);
            next.set('step', 'data-editor');
            next.set('usecaseId', file.usecaseId);
            next.set('tab', reviewUnlabeledTab);
            next.set('labelFilter', 'unlabeled');
            setSearchParams(next);
            return;
        }
        if (!reviewUnlabeledUsecase) return;
        const url = taskUrlForUsecase(reviewUnlabeledUsecase, {
            objective: reviewUnlabeledUsecase._id,
            action: 'data-selection',
        });
        if (url) navigate(`${url}&tab=${reviewUnlabeledTab}&labelFilter=unlabeled`);
    };

    if (isDownload) {
        const downloadSteps = deriveDownloadSteps(file);
        const pctActiveStyle = { fontWeight: 600, fontVariantNumeric: 'tabular-nums' };
        const isReadyToSave = file.phase === 'ready_to_save';

        const handleSaveClick = async () => {
            if (!triggerModelDownloadSave || saveStarting) return;
            setSaveStarting(true);
            try {
                await triggerModelDownloadSave(progressKey);
            } catch (err) {
                sparkToast.error(err?.message || 'Save failed');
            } finally {
                setSaveStarting(false);
            }
        };

        const renderStepPercent = (state, percent) => {
            if (state === 'pending') return '0%';
            if (state === 'active') {
                return (
                    <span style={{ ...pctActiveStyle, color: COLORS.activeText }}>
                        {`${Math.max(0, Math.min(100, Math.round(percent)))}%`}
                    </span>
                );
            }
            if (state === 'complete') return '100%';
            return <span style={{ color: COLORS.error }}>!</span>;
        };

        const renderDownloadStepRight = () => {
            if (isReadyToSave) {
                return (
                    <button
                        type="button"
                        onClick={handleSaveClick}
                        disabled={saveStarting}
                        style={{
                            background: COLORS.primaryBlue,
                            color: '#fff',
                            border: 'none',
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: saveStarting ? 'not-allowed' : 'pointer',
                            opacity: saveStarting ? 0.7 : 1,
                        }}
                    >
                        {saveStarting ? 'Opening…' : 'Save to disk'}
                    </button>
                );
            }
            return renderStepPercent(downloadSteps.downloadState, downloadSteps.downloadPercent);
        };

        const handleCopyPath = () => {
            if (!file.localFilePath) return;
            try {
                navigator.clipboard.writeText(file.localFilePath);
                sparkToast.success('Path copied to clipboard');
            } catch (err) {
                sparkToast.error('Could not copy path');
            }
        };

        return (
            <div
                style={{
                    paddingBottom: 4,
                    marginBottom: isLast ? 0 : 14,
                    borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
                }}
            >
                {showDatasetSubtitle ? (
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.mutedText, marginBottom: 10 }}>
                        {progressBarDisplayName(datasetName)}
                    </div>
                ) : null}
                {downloadSteps.useTwoStep ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <StepRow
                            state={downloadSteps.buildState}
                            label={downloadSteps.buildLabel}
                            rightSlot={renderStepPercent(
                                downloadSteps.buildState,
                                downloadSteps.buildPercent
                            )}
                        />
                        <StepRow
                            state={downloadSteps.downloadState}
                            label={downloadSteps.downloadLabel}
                            rightSlot={renderDownloadStepRight()}
                        />
                    </div>
                ) : (
                    (() => {
                        const p = Math.max(0, Math.min(100, Math.round(file.progress ?? 0)));
                        const rowState = file.isError ? 'error' : p >= 100 && !file.isError ? 'complete' : 'active';
                        const rowLabel = file.message || 'Downloading file';
                        return (
                            <StepRow
                                state={rowState}
                                label={rowLabel}
                                rightSlot={renderStepPercent(rowState, p)}
                            />
                        );
                    })()
                )}
                {file.localFilePath ? (
                    <div
                        style={{
                            marginTop: 10,
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: 'rgba(59, 130, 246, 0.06)',
                            border: '1px solid rgba(59, 130, 246, 0.25)',
                            fontSize: 12,
                            lineHeight: 1.5,
                            color: COLORS.activeText,
                        }}
                    >
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            File saved on disk{file.sizeBytes ? ` (${formatBytes(file.sizeBytes)})` : ''}
                        </div>
                        <div
                            style={{
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                fontSize: 11,
                                color: COLORS.mutedText,
                                wordBreak: 'break-all',
                                marginBottom: 8,
                            }}
                        >
                            {file.localFilePath}
                        </div>
                        <button
                            type="button"
                            onClick={handleCopyPath}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                background: COLORS.primaryBlue,
                                border: 'none',
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '6px 10px',
                                borderRadius: 8,
                                cursor: 'pointer',
                            }}
                        >
                            <SparkIcon slug="copy" size="small" /> Copy path
                        </button>
                    </div>
                ) : null}
                {(file.phase === 'complete' ||
                    file.isCompleted ||
                    file.isError ||
                    (file.progress >= 100 && !downloadSteps.useTwoStep) ||
                    (file.phase === 'downloading' && file.downloadIndeterminate)) && (
                        <button
                            type="button"
                            onClick={() => onRemove(file.downloadId || progressKey)}
                            style={{
                                marginTop: 8,
                                alignSelf: 'flex-end',
                                background: 'none',
                                border: 'none',
                                color: COLORS.mutedText,
                                fontSize: 12,
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Dismiss
                        </button>
                    )}
                {isError && (
                    <div
                        style={{
                            marginTop: 10,
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: 'rgba(220, 38, 38, 0.06)',
                            border: `1px solid rgba(220, 38, 38, 0.25)`,
                            fontSize: 13,
                            color: '#991B1B',
                            lineHeight: 1.45,
                            userSelect: 'text',
                        }}
                    >
                        {message || 'Download failed'}
                        {details ? (
                            <button
                                type="button"
                                onClick={() => setDetailsOpen(!detailsOpen)}
                                style={{
                                    display: 'block',
                                    marginTop: 8,
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: 12,
                                    color: COLORS.controlIcon,
                                    userSelect: 'none',
                                }}
                            >
                                {detailsOpen ? 'Hide details' : 'Show details'}
                            </button>
                        ) : null}
                        {detailsOpen && details ? (
                            <pre
                                style={{
                                    margin: '8px 0 0',
                                    fontSize: 11,
                                    maxHeight: 96,
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    userSelect: 'text',
                                }}
                            >
                                {details}
                            </pre>
                        ) : null}
                    </div>
                )}
            </div>
        );
    }

    if (isRetrievalAgent) {
        const steps = deriveRetrievalAgentSteps(file);
        const showUploadGroup = shouldShowRetrievalUploadGroup(file);
        return (
            <div
                style={{
                    paddingBottom: 4,
                    marginBottom: isLast ? 0 : 14,
                    borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
                }}
            >
                {showDatasetSubtitle ? (
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.mutedText, marginBottom: 10 }}>
                        {progressBarDisplayName(datasetName)}
                    </div>
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {steps.map((step) => (
                        <StepRow
                            key={step.id}
                            state={step.state}
                            label={step.label}
                            subtitle={step.subtitle}
                            rightSlot={null}
                        />
                    ))}
                    {showUploadGroup ? <DatasetUploadStepRows file={file} isGeneration={false} /> : null}
                </div>
                {isError && (message || details) ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: COLORS.error }}>
                        {message || details}
                    </div>
                ) : null}
                {!isError && file.isCompleted && file.summary ? (
                    <div
                        data-testid="generation-summary"
                        style={{ marginTop: 8, fontSize: 12, color: isWarning ? COLORS.warning : COLORS.mutedText }}
                    >
                        {file.summary}
                    </div>
                ) : null}
                {(file.isCompleted || isError) ? (
                    <button
                        type="button"
                        onClick={() => onRemove(progressKey)}
                        style={{
                            marginTop: 8,
                            background: 'none',
                            border: 'none',
                            color: COLORS.mutedText,
                            fontSize: 12,
                            cursor: 'pointer',
                            padding: 0,
                        }}
                    >
                        Dismiss
                    </button>
                ) : null}
            </div>
        );
    }

    return (
        <div
            style={{
                paddingBottom: 4,
                marginBottom: isLast ? 0 : 14,
                borderBottom: isLast ? 'none' : '1px solid #F3F4F6',
            }}
        >
            {showDatasetSubtitle ? (
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.mutedText, marginBottom: 10 }}>
                    {progressBarDisplayName(datasetName)}
                </div>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <DatasetUploadStepRows file={file} isGeneration={isGeneration} />
            </div>
            {isActiveUpload ? (
                <>
                    <button
                        type="button"
                        onClick={() => setCancelConfirmOpen(true)}
                        style={{
                            marginTop: 10,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                            color: COLORS.primaryBlue,
                        }}
                    >
                        Cancel upload
                    </button>
                    <SparkModalPortal
                        open={cancelConfirmOpen}
                        onClose={() => setCancelConfirmOpen(false)}
                        size="text"
                        style={{ zIndex: BAR_OVERLAY_Z_INDEX }}
                    >
                        <Modal
                            feedbackType="warning"
                            title="Cancel upload?"
                            showSubtitle={false}
                            showScrollBar={false}
                            fixedHeight={false}
                            danger
                            primaryLabel="Cancel upload"
                            tertiaryLabel="Keep"
                            onPrimaryClick={() => {
                                setCancelConfirmOpen(false);
                                onCancelUpload(progressKey);
                            }}
                            onTertiaryClick={() => setCancelConfirmOpen(false)}
                            onCloseClick={() => setCancelConfirmOpen(false)}
                        >
                            <p style={{ margin: 0 }}>Stop this upload and remove it from the queue.</p>
                        </Modal>
                    </SparkModalPortal>
                </>
            ) : isActiveGeneration ? (
                <>
                    <button
                        type="button"
                        onClick={() => {
                            setKeepGeneratedRecords(true);
                            setCancelConfirmOpen(true);
                        }}
                        style={{
                            marginTop: 10,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                            color: COLORS.primaryBlue,
                        }}
                    >
                        Cancel generation
                    </button>
                    <SparkModalPortal
                        open={cancelConfirmOpen}
                        onClose={() => setCancelConfirmOpen(false)}
                        size="text"
                        style={{ zIndex: BAR_OVERLAY_Z_INDEX }}
                    >
                        <Modal
                            feedbackType="warning"
                            title="Cancel generation?"
                            showSubtitle={false}
                            showScrollBar={false}
                            fixedHeight={false}
                            danger
                            primaryLabel="Cancel generation"
                            tertiaryLabel="Keep"
                            onPrimaryClick={() => {
                                setCancelConfirmOpen(false);
                                onCancelGeneration(progressKey, {
                                    keepGeneratedRecords,
                                });
                            }}
                            onTertiaryClick={() => setCancelConfirmOpen(false)}
                            onCloseClick={() => setCancelConfirmOpen(false)}
                        >
                            <p style={{ margin: 0, marginBottom: showKeepRecordsCheckbox ? 12 : 0 }}>
                                Stop this data generation process
                            </p>
                            {showKeepRecordsCheckbox ? (
                                <Checkbox
                                    label="Keep this generation's records"
                                    defaultStatus="active"
                                    status={keepGeneratedRecords ? 'active' : 'inactive'}
                                    onChange={() => setKeepGeneratedRecords((previous) => !previous)}
                                />
                            ) : null}
                        </Modal>
                    </SparkModalPortal>
                </>
            ) : (
                <>
                    {!isError && !isWarning && file.isCompleted && file.summary ? (
                        <div
                            data-testid="generation-summary"
                            style={{ marginTop: 8, fontSize: 12, color: COLORS.mutedText, userSelect: 'text' }}
                        >
                            {file.summary}
                        </div>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => onRemove(progressKey)}
                        style={{
                            marginTop: 8,
                            background: 'none',
                            border: 'none',
                            color: COLORS.mutedText,
                            fontSize: 12,
                            cursor: 'pointer',
                            padding: 0,
                        }}
                    >
                        Dismiss
                    </button>
                </>
            )}
            {isError && (
                <div
                    style={{
                        marginTop: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: 'rgba(220, 38, 38, 0.06)',
                        border: `1px solid rgba(220, 38, 38, 0.25)`,
                        fontSize: 13,
                        color: '#991B1B',
                        lineHeight: 1.45,
                        userSelect: 'text',
                    }}
                >
                    {message || 'Something went wrong'}
                    {details ? (
                        <button
                            type="button"
                            onClick={() => setDetailsOpen(!detailsOpen)}
                            style={{
                                display: 'block',
                                marginTop: 8,
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: 12,
                                color: COLORS.controlIcon,
                                userSelect: 'none',
                            }}
                        >
                            {detailsOpen ? 'Hide details' : 'Show details'}
                        </button>
                    ) : null}
                    {detailsOpen && details ? (
                        <pre
                            style={{
                                margin: '8px 0 0',
                                fontSize: 11,
                                maxHeight: 96,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                userSelect: 'text',
                            }}
                        >
                            {details}
                        </pre>
                    ) : null}
                </div>
            )}
            {isWarning && !isError && (
                <div
                    style={{
                        marginTop: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: 'rgba(217, 119, 6, 0.08)',
                        border: `1px solid rgba(217, 119, 6, 0.3)`,
                        fontSize: 13,
                        color: '#92400E',
                        lineHeight: 1.45,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        // The draggable card shell disables selection; the warning
                        // copy (title, counts, error details) must stay copyable.
                        userSelect: 'text',
                    }}
                >
                    <span style={{ color: COLORS.warning, marginTop: 2, flexShrink: 0, display: 'inline-flex' }}>
                        <SparkIcon slug="circle-alert-triangle" size="small" />
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontWeight: 600 }}>
                            {file.warningTitle || "Some labels couldn't be generated"}
                        </div>
                        <div style={{ whiteSpace: 'pre-line' }}>
                            {warningMessage || 'Some examples could not be labeled'}
                        </div>
                        {!file.warningTitle && (
                            <div>
                                The failed samples were marked as Unlabeled.
                                {canReviewUnlabeled ? (
                                    <span style={{ marginLeft: 4, display: 'inline-flex', verticalAlign: 'text-bottom' }}>
                                        <Button
                                            variant="link"
                                            size="small"
                                            type="button"
                                            onClick={handleReviewUnlabeled}
                                        >
                                            Review them manually
                                        </Button>
                                    </span>
                                ) : (
                                    ' Review them manually.'
                                )}
                            </div>
                        )}
                        {warningDetailLines.length > 0 ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setWarningDetailsOpen(!warningDetailsOpen)}
                                    style={{
                                        display: 'block',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: COLORS.controlIcon,
                                        userSelect: 'none',
                                        textAlign: 'left',
                                    }}
                                >
                                    {warningDetailsOpen ? 'Hide details' : 'Show details'}
                                </button>
                                {warningDetailsOpen ? (
                                    <pre
                                        style={{
                                            margin: 0,
                                            fontSize: 11,
                                            maxHeight: 96,
                                            overflow: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            userSelect: 'text',
                                        }}
                                    >
                                        {warningDetailLines.join('\n')}
                                    </pre>
                                ) : null}
                            </>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
}

const useDraggable = () => {
    const [position, setPosition] = useState({ x: 0, y: TOP_NAV_OFFSET });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const elementRef = useRef(null);

    const snapTopRight = useCallback(() => {
        const w = elementRef.current?.getBoundingClientRect().width ?? CARD_WIDTH;
        setPosition({
            x: Math.max(VIEWPORT_MARGIN, window.innerWidth - w - VIEWPORT_MARGIN),
            y: TOP_NAV_OFFSET,
        });
    }, []);

    useEffect(() => {
        snapTopRight();
        const onResize = () => snapTopRight();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [snapTopRight]);

    const handleMouseDown = useCallback((e) => {
        const target = e.target;
        if (!target.closest('[data-fpb-drag]')) return;
        if (target.closest('button') || target.closest('a')) return;

        e.preventDefault();
        setIsDragging(true);
        const rect = elementRef.current?.getBoundingClientRect();
        if (!rect) return;
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    }, []);

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDragging || !elementRef.current) return;
            e.preventDefault();
            const rect = elementRef.current.getBoundingClientRect();
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            const maxX = window.innerWidth - rect.width - VIEWPORT_MARGIN;
            const maxY = window.innerHeight - rect.height - VIEWPORT_MARGIN;
            setPosition({
                x: Math.max(VIEWPORT_MARGIN, Math.min(newX, maxX)),
                y: Math.max(VIEWPORT_MARGIN, Math.min(newY, maxY)),
            });
        },
        [isDragging, dragOffset]
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (!isDragging) return undefined;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const ensurePositionInBounds = useCallback(() => {
        if (!elementRef.current) return;
        const rect = elementRef.current.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width - VIEWPORT_MARGIN;
        const maxY = window.innerHeight - rect.height - VIEWPORT_MARGIN;
        setPosition((prev) => ({
            x: Math.max(VIEWPORT_MARGIN, Math.min(prev.x, maxX)),
            y: Math.max(VIEWPORT_MARGIN, Math.min(prev.y, maxY)),
        }));
    }, []);

    return {
        position,
        isDragging,
        elementRef,
        handleMouseDown,
        ensurePositionInBounds,
        snapTopRight,
    };
};

const useProgressManager = (progressStatus) => {
    const [visible, setVisible] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [closed, setClosed] = useState(false);
    const lastActiveFilesRef = useRef(new Set());
    const hideTimeoutRef = useRef(null);

    const relevantFiles = useMemo(() => {
        if (!progressStatus) return [];
        return Object.entries(progressStatus)
            .map(([progressKey, value]) => ({ ...value, progressKey }))
            .sort((a, b) => {
                const aActive = isProgressFileActive(a);
                const bActive = isProgressFileActive(b);
                if (aActive && !bActive) return -1;
                if (bActive && !aActive) return 1;
                if (a.progress < 100 && !a.isError && (b.progress === 100 || b.isError)) return -1;
                if (b.progress < 100 && !b.isError && (a.progress === 100 || a.isError)) return 1;
                if (a.isError && !b.isError) return 1;
                if (b.isError && !a.isError) return -1;
                const nameA = a.datasetName || '';
                const nameB = b.datasetName || '';
                return nameA.localeCompare(nameB);
            });
    }, [progressStatus]);

    useEffect(() => {
        const activeFiles = relevantFiles.filter((f) => isProgressFileActive(f));
        const completedFiles = relevantFiles.filter((f) => isProgressFileComplete(f));
        const errorFiles = relevantFiles.filter((f) => f.isError);
        const warningFiles = relevantFiles.filter((f) => isProgressFileWarning(f));

        const currentActiveFileNames = new Set(activeFiles.map((f) => f.progressKey));
        const wasActiveFileNames = lastActiveFilesRef.current;
        const hasNewActiveFiles = [...currentActiveFileNames].some((name) => !wasActiveFileNames.has(name));
        if (hasNewActiveFiles) {
            setMinimized(false);
            setClosed(false);
        }
        lastActiveFilesRef.current = currentActiveFileNames;

        // Entries that surface a local filesystem path (e.g. Docker tar saved on disk)
        // must stay pinned until the user copies the path; suppress the 3s auto-hide.
        // Warning entries (partial label-generation success) are likewise pinned so
        // the user has time to read how many examples were skipped.
        const hasPinnedLocalPath = completedFiles.some((f) => Boolean(f.localFilePath));
        const hasWarning = warningFiles.length > 0;
        const hasActiveBrowserDownload = activeFiles.some(
            (f) => f.isDownload && (f.phase === 'downloading' || f.phase === 'ready_to_save')
        );

        if (activeFiles.length > 0 || errorFiles.length > 0) {
            setVisible(true);
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
        } else if (completedFiles.length > 0) {
            setVisible(true);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
            if (!hasPinnedLocalPath && !hasActiveBrowserDownload && !hasWarning) {
                hideTimeoutRef.current = setTimeout(() => setVisible(false), 3000);
            }
        } else {
            setVisible(false);
        }

        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
        };
    }, [relevantFiles]);

    return {
        visible,
        minimized,
        closed,
        relevantFiles,
        setMinimized,
        setClosed,
    };
};

const cardShellStyle = (position, isDragging) => ({
    position: 'fixed',
    left: position.x,
    top: position.y,
    zIndex: BAR_Z_INDEX,
    width: CARD_WIDTH,
    maxWidth: `min(${CARD_WIDTH}px, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`,
    maxHeight: 'min(520px, calc(100vh - 96px))',
    background: COLORS.cardBg,
    borderRadius: 16,
    boxShadow:
        '0 4px 24px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.06)',
    padding: '20px 22px 18px',
    display: 'flex',
    flexDirection: 'column',
    transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
    cursor: isDragging ? 'grabbing' : 'default',
    userSelect: 'none',
    fontFamily: 'var(--typography-font-family-platform)',
});

const minimizedStyle = (position, isDragging) => ({
    position: 'fixed',
    left: position.x,
    top: position.y,
    zIndex: BAR_Z_INDEX,
    minWidth: 200,
    maxWidth: `min(320px, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`,
    background: COLORS.cardBg,
    borderRadius: 14,
    boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.06)',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: isDragging ? 'grabbing' : 'pointer',
    userSelect: 'none',
    fontFamily: 'var(--typography-font-family-platform)',
});

export default function FloatingProgressBar() {
    const { progressStatus, removeProgressItem, cancelUpload, cancelGeneration } = useContext(GlobalContext);

    const { position, isDragging, elementRef, handleMouseDown, ensurePositionInBounds, snapTopRight } = useDraggable();
    const { visible, minimized, closed, relevantFiles, setMinimized, setClosed } = useProgressManager(progressStatus);

    const activeCount = relevantFiles.filter((f) => isProgressFileActive(f)).length;
    const warningCount = relevantFiles.filter((f) => isProgressFileWarning(f)).length;
    const completedCount = relevantFiles.filter(
        (f) => isProgressFileComplete(f) && !isProgressFileWarning(f)
    ).length;
    const errorCount = relevantFiles.filter((f) => f.isError).length;

    const hasPinnedLocalPath = relevantFiles.some((f) => Boolean(f.localFilePath));
    const hasActiveBrowserDownload = relevantFiles.some(
        (f) => f.isDownload && isProgressFileActive(f) && (f.phase === 'downloading' || f.phase === 'ready_to_save')
    );

    useEffect(() => {
        if (
            completedCount > 0 &&
            activeCount === 0 &&
            errorCount === 0 &&
            warningCount === 0 &&
            visible &&
            !closed &&
            !minimized &&
            !hasPinnedLocalPath &&
            !hasActiveBrowserDownload
        ) {
            const timer = setTimeout(() => setClosed(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [
        completedCount,
        activeCount,
        errorCount,
        warningCount,
        visible,
        closed,
        minimized,
        hasPinnedLocalPath,
        hasActiveBrowserDownload,
        setClosed,
    ]);

    useEffect(() => {
        if (!closed && !minimized && visible) {
            const timer = setTimeout(() => ensurePositionInBounds(), 80);
            return () => clearTimeout(timer);
        }
    }, [relevantFiles.length, closed, minimized, visible, ensurePositionInBounds]);

    useEffect(() => {
        if (!elementRef.current || closed || minimized || !visible) return undefined;
        let debounceTimer;
        const ro = new ResizeObserver(() => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => ensurePositionInBounds(), 50);
        });
        ro.observe(elementRef.current);
        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            ro.disconnect();
        };
    }, [closed, minimized, visible, ensurePositionInBounds]);

    if (!visible || relevantFiles.length === 0 || closed) {
        return null;
    }

    const primary =
        relevantFiles.find((f) => isProgressFileActive(f)) || relevantFiles[0];

    const headerTypography = {
        fontSize: 15,
        fontWeight: 700,
        color: COLORS.headerText,
        lineHeight: '22px',
        letterSpacing: '-0.01em',
    };

    const renderExpandedHeaderTitle = () => {
        if (relevantFiles.length > 1) {
            return (
                <div style={{ ...headerTypography, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {`Processing ${relevantFiles.length} items`}
                </div>
            );
        }
        if (primary?.isDownload) {
            const name = primary.datasetName || 'file';
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        width: '100%',
                        columnGap: 6,
                    }}
                >
                    <span style={{ ...headerTypography, flexShrink: 0 }}>Downloading</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TruncatedText
                            text={name}
                            tooltipText={name}
                            maxWidth="100%"
                            zIndex={BAR_OVERLAY_Z_INDEX}
                            style={headerTypography}
                        />
                    </div>
                </div>
            );
        }
        const rawName = primary?.datasetName || '';
        const name = progressBarDisplayName(rawName);
        const checkingLabels = primary?.isGeneration && (
            primary.phase === 'checking_labels' || primary.generationPhase === 'checking_labels'
        );
        const generatingTrajectories =
            primary?.phase === 'label_generation' && primary?.isTrajectoryGeneration;
        const headerVerb = checkingLabels
            ? 'Checking labels for'
            : generatingTrajectories
                ? 'Generating trajectories for'
                : primary?.isRetrievalAgent
                    ? 'Retrieving'
                    : primary?.isGeneration
                        ? 'Generating'
                        : primary?.phase === 'label_generation'
                            ? 'Generating labels for'
                            : 'Processing';
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    width: '100%',
                    columnGap: 6,
                }}
            >
                <span style={{ ...headerTypography, flexShrink: 0 }}>{headerVerb}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <TruncatedText
                        text={name}
                        tooltipText={rawName || name}
                        maxWidth="100%"
                        zIndex={BAR_OVERLAY_Z_INDEX}
                        style={headerTypography}
                    />
                </div>
            </div>
        );
    };

    // Portal to <body>: the bar is mounted inside the app layout, so any
    // ancestor that creates a stacking context would trap it below the
    // drawer / modal portals no matter how high its own z-index is.
    if (minimized) {
        return createPortal(
            <div
                ref={elementRef}
                style={minimizedStyle(position, isDragging)}
                onMouseDown={handleMouseDown}
                data-fpb-drag
            >
                    <span style={{ color: COLORS.controlIcon, display: 'inline-flex' }}>
                        <SparkIcon slug="upload" size="small" />
                    </span>
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: COLORS.headerText,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {activeCount > 0 &&
                            `${activeCount} ${relevantFiles.some((f) => f.isDownload) ? 'downloading' : 'in progress'}`}
                        {completedCount > 0 &&
                            (activeCount > 0 ? ` · ${completedCount} done` : `${completedCount} done`)}
                        {warningCount > 0 &&
                            (activeCount > 0 || completedCount > 0 ? ` · ${warningCount} warning` : `${warningCount} warning`)}
                        {errorCount > 0 &&
                            (activeCount > 0 || completedCount > 0 || warningCount > 0 ? ` · ${errorCount} failed` : `${errorCount} failed`)}
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                            setMinimized(false);
                            setTimeout(() => snapTopRight(), 0);
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: 14,
                            cursor: 'pointer',
                            color: COLORS.controlIcon,
                            padding: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 6,
                        }}
                        title="Expand"
                    >
                        <SparkIcon slug="chevron-right" size="small" />
                    </button>
            </div>,
            document.body
        );
    }

    return createPortal(
        <div
            ref={elementRef}
            style={cardShellStyle(position, isDragging)}
            onMouseDown={handleMouseDown}
        >
                <div
                    data-fpb-drag
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 12,
                        marginBottom: 16,
                        cursor: 'grab',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            minWidth: 0,
                            paddingRight: 4,
                        }}
                    >
                        {renderExpandedHeaderTitle()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <button
                            type="button"
                            onClick={() => {
                                setMinimized(true);
                                setTimeout(() => snapTopRight(), 0);
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 6,
                                cursor: 'pointer',
                                color: COLORS.controlIcon,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 8,
                                lineHeight: 1,
                            }}
                            title="Minimize"
                        >
                            <SparkIcon slug="minus" size="small" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setClosed(true)}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 6,
                                cursor: 'pointer',
                                color: COLORS.controlIcon,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 8,
                                lineHeight: 1,
                            }}
                            title="Close"
                        >
                            <SparkIcon slug="close" size="small" />
                        </button>
                    </div>
                </div>

                <div
                    style={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: 1,
                        marginRight: -4,
                        paddingRight: 4,
                    }}
                >
                    {relevantFiles.map((file, index) => (
                        <DatasetProgressCard
                            key={file.progressKey}
                            file={file}
                            showDatasetSubtitle={relevantFiles.length > 1}
                            isLast={index === relevantFiles.length - 1}
                            onRemove={removeProgressItem}
                            onCancelUpload={cancelUpload}
                            onCancelGeneration={cancelGeneration}
                        />
                    ))}
                </div>
            </div>,
        document.body
    );
}
